import type { ContextualData, JourneyEventContextualData } from './contextualData';
import { emptyJourneyEventContextualData } from './contextualData';
import type {
  DatadogRumClientInstance,
  JourneyClientInstance,
  SentryClientInstance,
} from './declarations/Clients';
import type { EventTrackingAction } from './declarations/EventTracking';
import type { CategoryName, EventData, EventName } from './declarations/JourneyEvent';
import type {
  Observability,
  TrackingOptions,
  TrackJourneyEventOptions,
} from './declarations/Observability';
import type { UserEvent } from './declarations/UserEvent';

// Custom instance of Observability that delegates methods to an internal instance.
// This is so we don't have to deal with the global instance being null or the
// global instance being a reassignable variable.
// It also enables people to save a reference to the global instance before they
// actually initialize it.
class ProxyObservabilityInstance<A, B> implements Observability<A, B> {
  currentInstance: Observability<A, B> | null = null;

  setCurrentInstance(instance: Observability<A, B>) {
    this.currentInstance = instance;
  }

  // This is only used for testing.
  _destroyCurrentInstance() {
    this.currentInstance = null;
  }

  /**
   * @deprecated This API sends legacy Segment analytics. Use `trackJourneyEvent` for
   * durable product behavior, `trackInternalEvent` for engineering diagnostics,
   * `trackError` for failures, or `markTiming` for durations.
   */
  trackEvent(
    // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
    action: EventTrackingAction<B>,
    label?: string,
    contextualData?: Partial<ContextualData>,
    options?: TrackingOptions<A>,
  ): void {
    // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
    this.currentInstance?.trackEvent(action, label, contextualData, options);
  }

  /**
   * @deprecated Use `trackJourneyEvent` for durable product behavior.
   * @see https://knowledgebase.apps.opendoor.com/doc/product/customer-demand/product-eng-org/operations-runbooks/how-to-create-journey-2-0-events.md
   */
  trackUserEvent(event: UserEvent, contextualData?: Partial<ContextualData>): void {
    // oxlint-disable-next-line typescript/no-deprecated -- The compatibility API stays available until consumer calls are removed
    this.currentInstance?.trackUserEvent(event, contextualData);
  }

  trackJourneyEvent<C extends CategoryName, E extends EventName<C>>(
    categoryName: C,
    eventName: E,
    eventData: EventData<C, E>,
    contextualData: JourneyEventContextualData = emptyJourneyEventContextualData,
    options?: TrackJourneyEventOptions,
  ): void {
    this.currentInstance?.trackJourneyEvent(
      categoryName,
      eventName,
      eventData,
      contextualData,
      options,
    );
  }

  trackError(
    label: string,
    error?: Error,
    contextualData?: Partial<ContextualData>,
    options?: TrackingOptions<A>,
  ): void {
    this.currentInstance?.trackError(label, error, contextualData, options);
  }

  trackInternalEvent(name: string, contextualData?: Partial<ContextualData>): void {
    this.currentInstance?.trackInternalEvent(name, contextualData);
  }

  markTiming(name: string, time?: number): void {
    this.currentInstance?.markTiming(name, time);
  }

  // All of the "getXXXClient" methods return a Proxy object, that capture requests
  // and then re-evaluate and forward the request to the currentInstance every time.
  // This ensures that if the currentInstance is changed, the new instance will be used.
  // And it also allows for behavior like:
  // import { Sentry } from '@opendoor/observability/sentry';
  // If you don't do this, and Sentry is delayed in initializing, then the user will
  // have a reference to an empty object forever, instead of a reference to an object
  // that will eventually have methods in it.
  // The only downside of this is that users have to handle undefined for the methods
  // but that tradeoff is okay.

  getDatadogRumClient(): DatadogRumClientInstance {
    return new Proxy(
      {},
      {
        get: (_, prop: string | symbol) => {
          const clientProp = prop as keyof DatadogRumClientInstance;
          return this.currentInstance
            ? this.currentInstance.getDatadogRumClient()[clientProp]
            : undefined;
        },
      },
    );
  }

  getJourneyUserClient(): JourneyClientInstance {
    return new Proxy(
      {},
      {
        get: (_, prop: string | symbol) => {
          const clientProp = prop as keyof JourneyClientInstance;
          return this.currentInstance
            ? this.currentInstance.getJourneyUserClient()[clientProp]
            : undefined;
        },
        set: (_, prop: string | symbol, value: any) => {
          const clientProp = prop as keyof JourneyClientInstance;
          return this.currentInstance
            ? (this.currentInstance.getJourneyUserClient()[clientProp] = value)
            : undefined;
        },
      },
    );
  }

  getJourneyEventClient(): JourneyClientInstance {
    return new Proxy(
      {},
      {
        get: (_, prop: string | symbol) => {
          const clientProp = prop as keyof JourneyClientInstance;
          return this.currentInstance
            ? this.currentInstance.getJourneyEventClient()[clientProp]
            : undefined;
        },
        set: (_, prop: string | symbol, value: any) => {
          const clientProp = prop as keyof JourneyClientInstance;
          return this.currentInstance
            ? (this.currentInstance.getJourneyEventClient()[clientProp] = value)
            : undefined;
        },
      },
    );
  }

  getSentryClient(): SentryClientInstance {
    return new Proxy(
      {},
      {
        get: (_, prop: string | symbol) => {
          const clientProp = prop as keyof SentryClientInstance;
          return this.currentInstance
            ? this.currentInstance.getSentryClient()[clientProp]
            : undefined;
        },
      },
    );
  }

  isInitialized(): boolean {
    return this.currentInstance !== null;
  }
}

/**
 * Global Observability instance, set by initObservability.
 * You probably want to use observability/react/useObservability instead of this variable directly.
 * If you want to check if it is initialized, use globalObservability.isInitialized.
 */
export const globalObservability: ProxyObservabilityInstance<
  string | undefined,
  string | undefined
> = (() => {
  const globalObservabilityKey = Symbol.for('opendoor.observability.global');
  const globalWithObservability = globalThis as typeof globalThis &
    Record<symbol, ProxyObservabilityInstance<string | undefined, string | undefined> | undefined>;
  const existing = globalWithObservability[globalObservabilityKey];
  if (existing) {
    return existing;
  }

  const instance = new ProxyObservabilityInstance<string | undefined, string | undefined>();
  Object.defineProperty(globalWithObservability, globalObservabilityKey, {
    configurable: true,
    value: instance,
    writable: true,
  });
  return instance;
})();
